import { INDUSTRY_OPTIONS, POSTED_OPTIONS, WORK_OPTIONS } from '../storage.js';
import { MAX_EXPERIENCE, MIN_EXPERIENCE } from '../jobboard-v3/constants/experience.js';
import { MAX_SALARY, MIN_SALARY } from '../jobboard-v3/constants/salary.js';

/**
 * The option objects a saved alert's meta value names, in the order the alert stored them.
 *
 * Saved meta is polymorphic by construction: the classic board writes `industry`/`work_setting`
 * as a scalar and v3 writes them as an array (`[]` when nothing is picked), so the plain
 * `getFilterByValue` lookup — `find((el) => el.value === item)` — misses every alert the live
 * board created. Same "not set" tolerance the alert identity check applies, resolved to options
 * instead of strings, so an unset filter comes back empty rather than as the `value: null` default.
 *
 * A value with no matching option is dropped: the options are the vocabulary, and an alert
 * saved against a retired one has nothing to show for it.
 */
export const alertFilterOptions = (options, value) => (Array.isArray(value) ? value : [value])
	.filter((item) => item !== null && item !== undefined && item !== '')
	.map((item) => options.find((option) => option.value === item))
	.filter(Boolean);

/** The name of an alert saved without a job title (EN-1304). */
export const UNTITLED_ALERT_NAME = 'Any job title';

/** What an alert without a location searches, as its location chip reads. */
export const ANYWHERE_IN_THE_US = 'Anywhere in the US';

/** The radius the board searches when an alert leaves it unset: RANGE_OPTIONS' default, "Within 40 miles". */
export const DEFAULT_ALERT_RADIUS = 40;

export const EMAIL_FREQUENCIES = ['daily', 'weekly', 'monthly'];

/** The placeholders older alerts were saved with when the search had no title or no location. */
const UNSET_ROLE = 'title not set';
const UNSET_LOCATION = 'not set';

// Lowercase inside a title, as Title Case leaves them: "Vice President of Sales".
const MINOR_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'with']);

const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

const toTitleCase = (text) => text
	.split(/\s+/)
	.map((word, index) => (index > 0 && MINOR_WORDS.has(word)
		? word
		: word.split(/([-/])/).map(capitalize).join('')))
	.join(' ');

/**
 * An alert's name: its job title, in Title Case.
 *
 * Titles used to be saved lowercased, which loses the user's own capitals. Only those all-lowercase titles are
 * converted, so a title saved as typed keeps them: "UX Designer" stays "UX Designer", where Title Case on
 * "ux designer" can only give "Ux Designer".
 */
export const alertName = (alert) => {
	const role = alert?.role?.trim() ?? '';

	if (role === '' || role.toLowerCase() === UNSET_ROLE) {
		return UNTITLED_ALERT_NAME;
	}

	return role === role.toLowerCase() ? toTitleCase(role) : role;
};

/** Whether an alert names a job title to search, rather than the placeholder older alerts were saved with. */
export const hasAlertTitle = (alert) => alertName(alert) !== UNTITLED_ALERT_NAME;

/** "Austin, TX" for an alert in "Austin, TX, USA", or null for an alert without a location. */
export const alertLocation = (alert) => {
	const location = alert?.location?.trim() ?? '';

	if (location === '' || location.toLowerCase() === UNSET_LOCATION) {
		return null;
	}

	return location.replace(/,\s*(USA|US|United States)$/i, '');
};

/** "in Austin, TX", or "anywhere in the US", to follow "{name} jobs" or "{Frequency} emails". */
export const alertLocationPhrase = (alert) => {
	const location = alertLocation(alert);

	return location ? `in ${location}` : 'anywhere in the US';
};

/** The alert's email frequency, `weekly` when it has none. */
export const alertFrequency = (alert) => {
	const frequency = alert?.meta?.email_frequency;

	return EMAIL_FREQUENCIES.includes(frequency) ? frequency : 'weekly';
};

export const frequencyLabel = (frequency) => frequency.charAt(0).toUpperCase() + frequency.slice(1);

const toNumber = (value) => (value === null || value === undefined || value === '' ? null : Number(value));

const yearsLabel = (years) => (years === 1 ? '1 year' : `${years} years`);

/**
 * "3+ years", "3–5 years", "Up to 5 years", or null when the alert leaves experience open. The board saves an unset
 * end as null, and older alerts saved the slider's own ends, so both read as unset.
 */
export const experienceLabel = ({ min_experience: minValue, max_experience: maxValue }) => {
	const min = toNumber(minValue);
	const max = toNumber(maxValue);
	const hasMin = min !== null && min > MIN_EXPERIENCE;
	const hasMax = max !== null && max < MAX_EXPERIENCE;

	if (hasMin && hasMax) {
		return min === max ? yearsLabel(min) : `${min}–${max} years`;
	}

	if (hasMin) {
		return `${min}+ years`;
	}

	return hasMax ? `Up to ${yearsLabel(max)}` : null;
};

const thousands = (amount) => `$${Math.round(amount / 1000)}K`;

/** "$120K+", "$80K–$120K", "Up to $150K", or null when the alert leaves salary open. */
export const salaryLabel = ({ min_salary: minValue, max_salary: maxValue }) => {
	const min = toNumber(minValue);
	const max = toNumber(maxValue);
	const hasMin = min !== null && min > MIN_SALARY;
	const hasMax = max !== null && max < MAX_SALARY;

	if (hasMin && hasMax) {
		return `${thousands(min)}–${thousands(max)}`;
	}

	if (hasMin) {
		return `${thousands(min)}+`;
	}

	return hasMax ? `Up to ${thousands(max)}` : null;
};

/**
 * The search an alert runs, in the board's flat filter vocabulary, for "View Matching Jobs" (EN-1304). Every field is
 * set, an unset one to the board's default, because the board takes the whole search from the alert.
 *
 * The location is kept as the alert saved it ("Austin, TX, USA"), with the coordinates its search was geocoded to.
 */
export const alertSearchFilters = (alert) => {
	const meta = alert?.meta ?? {};
	const location = alertLocation(alert) ? alert.location.trim() : null;

	return {
		jobTitle: hasAlertTitle(alert) ? alert.role.trim() : null,
		location,
		latitude: location ? (meta.latitude ?? null) : null,
		longitude: location ? (meta.longitude ?? null) : null,
		radius: toNumber(meta.radius),
		posted: meta.last_posted ?? null,
		industries: alertFilterOptions(INDUSTRY_OPTIONS, meta.industry),
		workSettings: alertFilterOptions(WORK_OPTIONS, meta.work_setting),
		experiences: [toNumber(meta.min_experience) ?? MIN_EXPERIENCE, toNumber(meta.max_experience) ?? MAX_EXPERIENCE],
		salaries: [toNumber(meta.min_salary) ?? MIN_SALARY, toNumber(meta.max_salary) ?? MAX_SALARY],
		autoApplyFilter: meta.auto_apply_filter === true,
	};
};

const optionTitles = (options, value) => alertFilterOptions(options, value).map((option) => option.title.trim());

/**
 * The chips under an alert's name, in the order EN-1304 lists them: location, work setting, experience, salary,
 * date posted, industry, then AutoApply. Location is always there; the rest only when the alert sets them. Each
 * work setting and industry is a chip of its own.
 *
 * @returns {Array<{key: string, label: string}>}
 */
export const alertChips = (alert) => {
	const meta = alert?.meta ?? {};
	const location = alertLocation(alert);
	const radius = toNumber(meta.radius) ?? DEFAULT_ALERT_RADIUS;
	const posted = POSTED_OPTIONS.find((option) => option.value !== null && option.value === meta.last_posted);

	return [
		{ key: 'location', label: location ? `${location} · ${radius} mi` : ANYWHERE_IN_THE_US },
		...optionTitles(WORK_OPTIONS, meta.work_setting).map((label) => ({ key: 'work_setting', label })),
		{ key: 'experience', label: experienceLabel(meta) },
		{ key: 'salary', label: salaryLabel(meta) },
		{ key: 'posted', label: posted?.title ?? null },
		...optionTitles(INDUSTRY_OPTIONS, meta.industry).map((label) => ({ key: 'industry', label })),
		{ key: 'auto_apply', label: meta.auto_apply_filter === true ? 'AutoApply jobs only' : null },
	].filter((chip) => chip.label !== null);
};

/** The shortest job function the Edit Job Alert form saves (EN-1305), as the server checks it too. */
export const JOB_FUNCTION_MIN_LENGTH = 2;

export const ALERT_FORM_ERRORS = {
	role: 'Enter a job function to search for, like Product Designer.',
	location: 'Pick a city from the list, or clear the field.',
};

/** What the Experience and Salary selects show for a saved range no preset describes (EN-1305). */
export const SAVED_RANGE = 'saved';

/**
 * @typedef {{ min: number|null, max: number|null }} AlertRange
 *
 * @typedef {object} AlertFormValues
 * @property {string} role
 * @property {string} location
 * @property {{ latitude: number, longitude: number }|null} coordinates the city picked from the list, or null
 * @property {number|null} radius
 * @property {Array<object>} industries
 * @property {Array<object>} workSettings
 * @property {string|null} posted
 * @property {AlertRange} experience
 * @property {AlertRange} salary
 * @property {boolean} autoApplyOnly
 * @property {'daily'|'weekly'|'monthly'} frequency
 */

/**
 * An alert as the Edit Job Alert form shows it (EN-1305).
 *
 * The location keeps its ", USA": `isSameJobAlert` compares the label, and the toggle saved it with one. It counts as
 * picked only with the coordinates the alert actually searches around; one saved without them searched the whole
 * country, so the form asks for the city again. Experience and salary keep the numbers saved, so a classic-board
 * range such as 2–5 years survives an edit to something else.
 *
 * @returns {AlertFormValues}
 */
export const alertFormValues = (alert) => {
	const meta = alert?.meta ?? {};
	const location = alertLocation(alert) ? alert.location.trim() : '';
	const latitude = toNumber(meta.latitude);
	const longitude = toNumber(meta.longitude);
	const coordinates = location !== '' && Number.isFinite(latitude) && Number.isFinite(longitude)
		? { latitude, longitude }
		: null;

	return {
		role: hasAlertTitle(alert) ? alertName(alert) : '',
		location,
		coordinates,
		radius: coordinates ? toNumber(meta.radius) : null,
		industries: alertFilterOptions(INDUSTRY_OPTIONS, meta.industry),
		workSettings: alertFilterOptions(WORK_OPTIONS, meta.work_setting),
		posted: meta.last_posted ?? null,
		experience: { min: toNumber(meta.min_experience), max: toNumber(meta.max_experience) },
		salary: { min: toNumber(meta.min_salary), max: toNumber(meta.max_salary) },
		autoApplyOnly: meta.auto_apply_filter === true,
		frequency: alertFrequency(alert),
	};
};

/**
 * The update the form sends for an alert (EN-1305).
 *
 * The rest of the alert's meta is kept. A cleared location is sent as "Not set", never empty: the server turns an
 * empty string into null and keeps the old location for a null one. Its coordinates and radius go with it, because
 * those, not the label, are what the alert searches.
 *
 * @param {AlertFormValues} values
 * @returns {{ role: string, location: string, meta: object }}
 */
export const alertFormPayload = (values, alert) => {
	const location = values.location.trim();

	return {
		role: values.role.trim(),
		location: location === '' ? 'Not set' : location,
		meta: {
			...alert?.meta,
			industry: values.industries.map((option) => option.value),
			work_setting: values.workSettings.map((option) => option.value),
			last_posted: values.posted,
			radius: values.coordinates ? values.radius : null,
			latitude: values.coordinates?.latitude ?? null,
			longitude: values.coordinates?.longitude ?? null,
			min_experience: values.experience.min,
			max_experience: values.experience.max,
			min_salary: values.salary.min,
			max_salary: values.salary.max,
			// The board saves `true` or leaves the key out.
			auto_apply_filter: values.autoApplyOnly || undefined,
			email_frequency: values.frequency,
		},
	};
};

/**
 * The fields the form cannot save as they are, with what to tell the user.
 *
 * @param {AlertFormValues} values
 * @returns {{ role?: string, location?: string }}
 */
export const alertFormErrors = (values) => {
	const errors = {};

	if (values.role.trim().length < JOB_FUNCTION_MIN_LENGTH) {
		errors.role = ALERT_FORM_ERRORS.role;
	}

	if (values.location.trim() !== '' && values.coordinates === null) {
		errors.location = ALERT_FORM_ERRORS.location;
	}

	return errors;
};

/**
 * The preset a saved range shows as: the "Any …" default (null) for an open range, the preset whose minimum it starts
 * at for an open-ended one, and SAVED_RANGE for anything else, such as the classic board's 2–5 years.
 *
 * @param {AlertRange} range
 * @param {Array<{ value: number|null }>} presets
 * @param {{ floor: number, ceiling: number }} bounds the slider's ends, which the board saved for "not set"
 * @returns {number|null|string}
 */
export const alertRangeChoice = (range, presets, { floor, ceiling }) => {
	if (range.max !== null && range.max < ceiling) {
		return SAVED_RANGE;
	}

	if (range.min === null || range.min <= floor) {
		return null;
	}

	return presets.some((option) => option.value === range.min) ? range.min : SAVED_RANGE;
};
